import TenantComponents from '@components';
import tenantConstants from '@constants';
import tenantTheme from '@theme';
import { Col, Layout, Row } from 'antd';
import cx from 'clsx';
import { t } from 'i18next';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import AppBanner from '../components/appBanner/appBanner';
import { Button, Icon } from '../components/common';
import { ChevronLeftIcon, ChevronRightIcon } from '../components/svg';
import AuthInfo from '../components/utilities/auth-info/info';
import { useGetLocation, useBlockingOverlayState, useCurrentPageTitle } from '../hooks';
import FeedbackTab from '../components/feedback/FeedbackTab';
import { setMobile } from '../store/appSlice';
import { TENANT_KEY } from '../utility/env';
import { isMobile as isMobileFunc } from '../utility/general';
import { isWebView } from '../utility/utility';
import LeadNudgeToasts from '../components/lead-nudges/LeadNudgeToasts';
import MenueItems from './MenueItems';
import { Div } from './style';

const { Header, Footer, Sider, Content } = Layout;
const SIDEBAR_COLLAPSED_WIDTH = 60;
const SIDEBAR_EXPANDED_WIDTH = 220;
// The left rail is stacked above the AntD drawer/modal mask (default z-index 1000), which is
// why it stays bright behind an open overlay. The dim scrim must sit above the rail to cover it.
const SIDEBAR_Z_INDEX = 1002;

const withAdminLayout = (WrappedComponent) => (props) => {
  const [collapsed, setCollapsed] = useState(true);
  const [hideTopBar, setHideTopBar] = useState(false);
  const { isMobile, darkMode, topMenu, rtl, isMemberArea } = useSelector((state) => state.app.AppConfig);
  const { user } = useSelector((state) => state.app.loginUser);
  const {
    hasMask: overlayHasMask,
    isOpen: isBlockingOverlayOpen,
    topZIndex: overlayTopZIndex,
    maskBackground: overlayMaskBackground,
  } = useBlockingOverlayState();
  // The persistent left rail only renders on desktop side-menu layouts. Member-area and
  // top-menu layouts have no left rail, and mobile uses an off-canvas drawer (out of scope),
  // so the dim-and-inert treatment applies to the desktop side rail only.
  const isDesktopSideRail = !isMemberArea && !topMenu && !isMobile;
  // Dim + inert while the overlay is actually open; released the instant it starts closing.
  const shouldCoverRail = isDesktopSideRail && isBlockingOverlayOpen;
  // Keep the scrim mounted while any mask exists (including during its exit animation) so it can
  // fade out in sync with the content overlay rather than unmounting abruptly.
  const railScrimMounted = isDesktopSideRail && overlayHasMask;
  const location = useGetLocation();
  const isDashboardPath = location.pathname.includes('/dashboard');

  const isClickToggleSidebarEnabled = tenantConstants.PUSH_CONTENT_ON_SIDEBAR_EXPAND;
  const shouldPushMainContent = !isMobile && !isMemberArea && !topMenu && isClickToggleSidebarEnabled;
  const sidebarOffset = shouldPushMainContent
    ? collapsed
      ? SIDEBAR_COLLAPSED_WIDTH
      : SIDEBAR_EXPANDED_WIDTH
    : SIDEBAR_COLLAPSED_WIDTH;
  // Content (including the post-listing form) is pushed by the expanded sidebar so it stays
  // centered in the remaining space; the width-capped, centered form re-centers rather than
  // shrinking as long as there's slack.
  const contentSidebarOffset = sidebarOffset;

  const dispatch = useDispatch();
  const controlRef = useRef();
  const railScrimRef = useRef(null);
  const completionModalSuccessRef = useRef();

  const headerContainerRef = useRef();
  const [minHeightOffset, setMinHeightOffset] = useState(0);
  const [isBannerVisible, setIsBannerVisible] = useState(
    isMobile && isDashboardPath && tenantConstants.PITCH_MOBILE_APP,
  );

  const currentPageTitle = useCurrentPageTitle();

  const profileCompletionScore = `showCompletionModal_${user?.id}`;
  const userTruBrokerDate = `truBrokerDate_${user?.id}`;

  useEffect(() => {
    const showBanner = JSON.parse(localStorage.getItem('showBanner'));
    setIsBannerVisible(showBanner && isMobile && isDashboardPath && tenantConstants.PITCH_MOBILE_APP);
  }, [isMobile, isDashboardPath]);

  const isTruBrokerForFirstTime = () => {
    return (
      tenantConstants.TRU_BROKER_ENABLED &&
      user?.is_tru_broker &&
      JSON.parse(localStorage.getItem(userTruBrokerDate)) != user?.tru_broker_start_date
    );
  };
  const showProfileSuccessPopUp = () => {
    return (
      user?.profile_completion?.score == 100 &&
      JSON.parse(localStorage.getItem(profileCompletionScore)) != user?.profile_completion?.score &&
      !isTruBrokerForFirstTime()
    );
  };

  const handleTruBrokerSuccessIntimation = () => {
    if (isTruBrokerForFirstTime()) {
      completionModalSuccessRef?.current && completionModalSuccessRef?.current?.showModal('isTruBroker');
      localStorage.setItem(userTruBrokerDate, JSON.stringify(user?.tru_broker_start_date));
    }
    localStorage.setItem(userTruBrokerDate, JSON.stringify(user?.tru_broker_start_date));
  };

  useEffect(() => {
    if (tenantConstants.PROFILE_COMPLETION_APPLICABLE && !isMemberArea && !isWebView()) {
      if (showProfileSuccessPopUp()) {
        completionModalSuccessRef?.current && completionModalSuccessRef?.current?.showModal('isProfileCompleted');
        localStorage.setItem(profileCompletionScore, JSON.stringify(user?.profile_completion?.score));
      }
      localStorage.setItem(profileCompletionScore, JSON.stringify(user?.profile_completion?.score));
      handleTruBrokerSuccessIntimation();
    }
  }, [user]);

  const updateDimensions = () => {
    setCollapsed(true);
    dispatch(setMobile(isMobileFunc()));
  };

  useEffect(() => {
    window.addEventListener('resize', updateDimensions);
    document.addEventListener('mousedown', handleClickOutside);
    updateDimensions();
    return () => {
      window.removeEventListener('resize', updateDimensions);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useLayoutEffect(() => {
    if (headerContainerRef?.current) {
      setMinHeightOffset(headerContainerRef?.current?.offsetHeight);
    }
  }, [headerContainerRef]);

  const handleClickOutside = (event) => {
    if (isClickToggleSidebarEnabled) return;
    if (controlRef && !controlRef?.current?.contains(event.target) && !collapsed) {
      setCollapsed(true);
    }
  };

  const handleBannerClose = () => {
    setIsBannerVisible(false);
    localStorage.setItem('showBanner', JSON.stringify(false));
  };

  useEffect(() => {
    if (isMobile && !collapsed) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, collapsed]);

  // While a blocking overlay is open, remove the whole rail (nav, language switcher, support
  // button) from the tab order and the accessibility tree via `inert` + `aria-hidden`. React 18
  // has no reliable `inert` prop, so we toggle the attributes on the rail container directly.
  // The cleanup always strips them so the rail is never stranded inert (e.g. session-expiry
  // redirect while an overlay is open).
  useEffect(() => {
    const railContainer = controlRef.current;
    if (!railContainer) return undefined;

    if (shouldCoverRail) {
      railContainer.setAttribute('inert', '');
      railContainer.setAttribute('aria-hidden', 'true');
    } else {
      railContainer.removeAttribute('inert');
      railContainer.removeAttribute('aria-hidden');
    }

    return () => {
      railContainer.removeAttribute('inert');
      railContainer.removeAttribute('aria-hidden');
    };
  }, [shouldCoverRail]);

  // Mirror the content overlay's exact opacity onto the rail scrim every frame while a mask is
  // present. AntD animates the mask's element opacity (0↔1) with its own curve/duration; copying
  // that value each frame makes the rail dim and undim in true lockstep with the content overlay
  // on both open and close — a separate CSS transition can only approximate the timing and easing.
  useLayoutEffect(() => {
    if (!railScrimMounted) return undefined;

    let frame = null;
    const mirror = () => {
      const scrim = railScrimRef.current;
      if (scrim) {
        const masks = document.querySelectorAll('.ant-drawer-mask, .ant-modal-mask');
        let topMask = null;
        let topZIndex = -Infinity;
        masks.forEach((mask) => {
          const computed = window.getComputedStyle(mask);
          if (computed.display === 'none' || computed.visibility === 'hidden') return;
          const zIndex = parseInt(computed.zIndex, 10) || 0;
          if (zIndex >= topZIndex) {
            topZIndex = zIndex;
            topMask = mask;
          }
        });
        scrim.style.opacity = topMask ? window.getComputedStyle(topMask).opacity : '0';
      }
      frame = window.requestAnimationFrame(mirror);
    };

    // Run once synchronously before paint so the scrim never flashes at full opacity on mount.
    mirror();

    return () => {
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [railScrimMounted]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev);
  };

  const toggleCollapsedMobile = () => {
    if (isMobileFunc()) {
      setCollapsed((prev) => !prev);
    }
  };

  const footerStyle = {
    padding: '12px 16px',
    color: 'rgba(0, 0, 0, 0.65)',
    fontSize: '14px',
    background: 'rgba(255, 255, 255, .90)',
    width: '100%',
    boxShadow: '0 -5px 10px rgba(146,153,184, 0.05)',
  };

  const sidebarTopOffset = isBannerVisible ? 66 : 0;

  const SideBarStyle = {
    margin: '0',
    paddingBlock: '4px 0',
    paddingInline: collapsed ? 0 : '12px',
    '--sidebar-brand-inset': collapsed ? (rtl ? '1px' : '20px') : '10px',
    overflow: 'visible',
    position: 'fixed',
    top: sidebarTopOffset,
    insetInlineStart: 0,
    bottom: 0,
    zIndex: SIDEBAR_Z_INDEX,
  };

  // Scrim geometry mirrors the rail exactly: same top offset, same current width
  // (collapsed vs expanded), so the rail is fully covered in either state (AC1, collapsed/
  // expanded edge case). RTL is handled by insetInlineStart, which flips to the right side.
  const railScrimWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;
  // Sit above both the rail and the topmost mask so the scrim actually dims the rail and
  // swallows clicks over it (the rail's z-index exceeds the mask's, so max + 1 is required).
  const railScrimZIndex = Math.max(overlayTopZIndex, SIDEBAR_Z_INDEX) + 1;

  const showSidebarPadding = !isMobile && !topMenu && !isMemberArea;

  const renderHeader = () => (
    <Header
      style={{
        position: 'fixed',
        top: isBannerVisible ? 66 : 0,
        insetInline: 0,
        display: 'flex',
        alignItems: 'center',
        minHeight: 60,
        maxHeight: 60,
        paddingBlock: 12,
        paddingInlineEnd: '20px',
        paddingInlineStart: showSidebarPadding ? `${sidebarOffset + 25}px` : '20px',
        boxSizing: 'border-box',
        lineHeight: 'normal',
        transition: 'padding-inline-start 220ms ease, padding-inline-end 220ms ease',
      }}
      ref={headerContainerRef}
    >
      <Row align="middle" justify="space-between" wrap={false} style={{ width: '100%' }} gutter={16}>
        <Col flex="none">
          <div className="align-center-v navbar-brand" style={{ gap: 12 }}>
            {isMobile ? (
              <Button
                type="link"
                onClick={toggleCollapsed}
                style={{ '--btn-content-color': '#000' }}
                title={collapsed ? t('Open side menu') : t('Close side menu')}
              >
                <Icon icon="MobileSidebarMenuIcon" size={24} />
              </Button>
            ) : null}

            {topMenu && !isMobile ? (
              <Link className={cx('top-menu', 'striking-logo')} to="/">
                <TenantComponents.Logo rtl={rtl} />
              </Link>
            ) : null}

            {!topMenu && currentPageTitle ? (
              <span
                className="navbar-page-title"
                style={{
                  fontWeight: 700,
                  fontSize: 20,
                  color: '#000',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                }}
              >
                {currentPageTitle}
              </span>
            ) : null}
          </div>
        </Col>

        {isMobile ? (
          <Col flex="auto">
            <div className="mobile-action">
              <AuthInfo />
            </div>
          </Col>
        ) : (
          <Col
            flex="auto"
            style={{ display: 'flex', justifyContent: 'flex-end', marginInlineEnd: !isMobile && '-8px' }}
          >
            <AuthInfo />
          </Col>
        )}
      </Row>
    </Header>
  );

  return (
    <Div
      darkMode={darkMode}
      mobileSidebarOpen={isMobile && !collapsed}
      theme={{ ...tenantTheme, topMenu: !!isMemberArea, isMobile, rtl }}
      style={{ paddingBlockStart: isBannerVisible ? '72px' : 0 }}
    >
      {isBannerVisible && (
        <AppBanner
          style={{ position: 'fixed', top: 0, insetInline: 0, zIndex: 1000 }}
          handleClose={handleBannerClose}
        />
      )}
      <Layout>
        {!isWebView() &&
          (isMemberArea ? (
            <div ref={headerContainerRef}>
              <TenantComponents.Layout.Header />
            </div>
          ) : (
            renderHeader()
          ))}

        {!isMemberArea &&
          (!topMenu || isMobile ? (
            <div ref={controlRef}>
              <ThemeProvider theme={tenantTheme?.darkTheme}>
                <Sider
                  width={SIDEBAR_EXPANDED_WIDTH}
                  collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
                  style={SideBarStyle}
                  collapsed={collapsed}
                  theme={!darkMode ? 'light' : 'dark'}
                  onMouseEnter={isClickToggleSidebarEnabled ? undefined : () => setCollapsed(false)}
                  onMouseLeave={isClickToggleSidebarEnabled ? undefined : () => setCollapsed(true)}
                  onClick={collapsed ? () => setCollapsed(false) : undefined}
                >
                  {collapsed && !isMobile && (
                    <button
                      type="button"
                      className={cx('sidebar-expand-btn', { rtl })}
                      onClick={() => setCollapsed(false)}
                      aria-label={t('Expand side menu')}
                      // Hide the rail toggle whenever a dim is present (mask mounted, incl. its
                      // fade-out): it protrudes past the rail edge, so any part beyond the scrim
                      // would float bright over the seam. Use `visibility` (no transition) so it
                      // snaps out/in instead of lingering behind the button's own opacity fade,
                      // and key it to the mask's presence so it never reappears through the dim.
                      style={{ visibility: railScrimMounted ? 'hidden' : undefined }}
                    >
                      <ChevronRightIcon size={16} />
                    </button>
                  )}
                  {!collapsed && (
                    <button
                      type="button"
                      className={cx('sidebar-expand-btn sidebar-collapse-btn', { rtl })}
                      onClick={() => setCollapsed(true)}
                      aria-label={t('Collapse side menu')}
                      style={{ visibility: railScrimMounted ? 'hidden' : undefined }}
                    >
                      <ChevronLeftIcon size={16} />
                    </button>
                  )}
                  <div className="sidebar-overview-icon" style={{ marginRight: !collapsed && rtl ? '-29px' : undefined }}>
                    <TenantComponents.Logo rtl={rtl} />
                  </div>
                  <div className="sidebar-menu-scroll">
                    <MenueItems
                      collapsed={collapsed}
                      toggleCollapsed={toggleCollapsedMobile}
                      handleTopBarSticky={(hide = false) => setHideTopBar(hide)}
                    />
                  </div>
                </Sider>
              </ThemeProvider>
            </div>
          ) : null)}

        {railScrimMounted && (
          <div
            ref={railScrimRef}
            className="rail-overlay-scrim"
            aria-hidden="true"
            // Swallow clicks over the rail so nothing navigates and the overlay stays open —
            // deliberately different from the content backdrop, which is left untouched (AC6).
            // While the overlay is closing (isOpen false) the rail is interactive again, so let
            // clicks pass through while the dim finishes fading out in sync with the content mask.
            // Opacity is driven imperatively by the mirror effect above, not via this style object.
            onMouseDown={(event) => isBlockingOverlayOpen && event.stopPropagation()}
            onClick={(event) => isBlockingOverlayOpen && event.stopPropagation()}
            style={{
              position: 'fixed',
              top: sidebarTopOffset,
              bottom: 0,
              insetInlineStart: 0,
              width: railScrimWidth,
              background: overlayMaskBackground,
              zIndex: railScrimZIndex,
              pointerEvents: isBlockingOverlayOpen ? 'auto' : 'none',
            }}
          />
        )}

        <Layout
          className="atbd-main-layout"
          style={{
            '--min-height-offset': `${minHeightOffset}px`,
            '--sidebar-offset': `${contentSidebarOffset}px`,
          }}
        >
          <Content>
            <WrappedComponent {...props} isMobile={isMobile} />
          </Content>

          {isMemberArea ? (
            <TenantComponents.Layout.Footer />
          ) : (
            <Footer className="admin-footer" style={footerStyle}>
              <Row>
                <Col xs={24} align="center">
                  <span className="admin-footer__copyright">
                    © {new Date().getFullYear()} –{' '}
                    {TENANT_KEY === 'zameen' ? t('Powered by Zameen.com') : t('All Rights Reserved')}
                  </span>
                </Col>
              </Row>
            </Footer>
          )}
        </Layout>
        {user?.id && <LeadNudgeToasts />}
        {user?.id && !isWebView() && (
          <FeedbackTab
            isBannerVisible={isBannerVisible}
            isMemberArea={isMemberArea}
            headerHeight={minHeightOffset}
            fabVisible={isMemberArea && !location.pathname.includes('/post-listing')}
          />
        )}
        {tenantConstants.PROFILE_COMPLETION_APPLICABLE && !isMemberArea && (
          <TenantComponents.ProfileCompletionCongratsModal ref={completionModalSuccessRef} />
        )}
      </Layout>
    </Div>
  );
};

export default withAdminLayout;
